/**
 * Same-origin IVR JSON client for the no-code designer.
 * CSRF + credentials duplicated here so api.ts stays untouched.
 */

const BASE = "";

export class IvrError extends Error {
  readonly status: number;
  readonly detail: string;
  readonly body: unknown;

  constructor(status: number, detail: string, body: unknown = null) {
    super(detail);
    this.name = "IvrError";
    this.status = status;
    this.detail = detail;
    this.body = body;
  }
}

function readCookie(name: string): string | null {
  const escaped = name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1");
  const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function csrfHeaders(): Record<string, string> {
  const token =
    readCookie("csrf_token") ||
    readCookie("csrf") ||
    readCookie("X-CSRF-Token") ||
    readCookie("csrftoken");
  if (!token) return {};
  return { "X-CSRF-Token": token };
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function str(value: unknown, fallback = ""): string {
  if (value === null || value === undefined) return fallback;
  return String(value);
}

function num(value: unknown, fallback: number): number {
  if (value === null || value === undefined || value === "") return fallback;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function boolish(value: unknown): boolean {
  if (value === true || value === 1 || value === "1" || value === "true") return true;
  return false;
}

function clipDetail(text: string, fallback: string): string {
  const t = text.trim();
  if (!t) return fallback;
  // Never surface a FastAPI 422 dump (JSON object/array as the message).
  if (t.startsWith("{") || t.startsWith("[")) return fallback;
  return t.length > 180 ? `${t.slice(0, 180)}…` : t;
}

function detailFromBody(body: unknown, fallback: string): string {
  if (typeof body === "string") return clipDetail(body, fallback);
  if (!body || typeof body !== "object") return fallback;
  const record = body as Record<string, unknown>;
  const detail = record.detail ?? record.message ?? record.error;
  if (typeof detail === "string") return clipDetail(detail, fallback);
  if (Array.isArray(detail) && detail.length) {
    const first = detail[0];
    if (typeof first === "string") return clipDetail(first, fallback);
    if (first && typeof first === "object") {
      const msg = (first as Record<string, unknown>).msg;
      if (typeof msg === "string") return clipDetail(msg, fallback);
    }
    return fallback;
  }
  return fallback;
}

async function parseBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

async function request<T>(path: string, options: { method?: string; body?: unknown } = {}): Promise<T> {
  const headers: Record<string, string> = {
    Accept: "application/json",
    ...csrfHeaders(),
  };
  const init: RequestInit = {
    method: options.method ?? (options.body !== undefined ? "POST" : "GET"),
    credentials: "include",
    headers,
  };
  if (options.body !== undefined) {
    headers["Content-Type"] = "application/json";
    init.body = JSON.stringify(options.body);
  }
  const response = await fetch(`${BASE}${path}`, init);
  const parsed = await parseBody(response);
  if (!response.ok) {
    throw new IvrError(
      response.status,
      detailFromBody(parsed, response.statusText || `HTTP ${response.status}`),
      parsed,
    );
  }
  return parsed as T;
}

/** Designer node types (form select). Extra types from office clones are preserved. */
export const DESIGNER_NODE_TYPES = [
  "start",
  "say",
  "dtmf",
  "hold",
  "wait",
  "wait_operator",
  "gate",
  "capture",
  "park",
  "hangup",
  "identity",
  "pickup",
  "label",
  "goto",
  "queue",
  "sms",
  "transfer",
] as const;

export const DISABLED_NODE_TYPES = ["queue", "sms", "transfer"] as const;

/** Never insert from a snippet button or a live snippet catalog. */
export const FORBIDDEN_SNIPPET_TYPES = [
  "originate",
  "sms",
  "transfer",
  "queue",
  "bridge",
  "esl",
  "conference",
  "javascript",
  "uuid_media",
  "uuid_audio",
] as const;

export const DEFAULT_DTMF_CHARSET = "0123456789#";

export type DesignerNodeType = (typeof DESIGNER_NODE_TYPES)[number];

export const EMPTY_GRAPH_HELPER = "starts from office verification; edit Code or Visual";

export interface IvrNode {
  id: string;
  type: string;
  label: string;
  prompt: string;
  min_digits: number;
  max_digits: number;
  terminator: string;
  timeout_s: number;
  sms_body: string;
  dest: string;
  kind: string;
  charset: string;
  file_id?: string;
  keys?: string;
  gateway?: string;
  cause?: string;
  match?: Record<string, string>;
  prompt_class?: string;
  hitl?: boolean;
  country?: string;
  lang?: string;
  target?: string;
  wait_for?: string;
}

export interface IvrEdge {
  id: string;
  source: string;
  target: string;
  when: string;
}

export interface IvrGraph {
  schema_version: number;
  id: string;
  name: string;
  start: string;
  nodes: IvrNode[];
  edges: IvrEdge[];
  system: boolean;
  description: string;
}

/** One-click Visual fragment. Nodes+edges only — never a full wipe. */
export interface IvrSnippet {
  id: string;
  label: string;
  description: string;
  nodes: IvrNode[];
  edges: IvrEdge[];
}

export interface IvrScriptSummary {
  slug: string;
  name: string;
  active_version: number;
  system: boolean;
  description: string;
  updated_at: string;
}

export interface IvrScript extends IvrScriptSummary {
  version: number;
  created_at: string;
  versions: number[];
  graph: IvrGraph;
}

export function nodeIsDisabled(type: string): boolean {
  return (DISABLED_NODE_TYPES as readonly string[]).includes(type);
}

export function isSystemScript(script: { slug?: string; system?: boolean } | null | undefined): boolean {
  if (!script) return false;
  return Boolean(script.system) || script.slug === "office";
}

export function ivrErrorMessage(err: unknown, fallback = "Request failed"): string {
  if (err instanceof IvrError) return err.detail || fallback;
  if (err instanceof Error) return err.message || fallback;
  return fallback;
}

export function isIvrUnavailable(err: unknown): boolean {
  return err instanceof IvrError && (err.status === 404 || err.status === 405);
}

function nextPrefixedId(prefix: string, existing: string[]): string {
  const known = new Set(existing);
  let n = existing.length + 1;
  let id = `${prefix}${n}`;
  while (known.has(id)) {
    n += 1;
    id = `${prefix}${n}`;
  }
  return id;
}

export function nextNodeId(nodes: IvrNode[]): string {
  return nextPrefixedId("n", nodes.map((node) => node.id));
}

export function nextEdgeId(edges: IvrEdge[]): string {
  return nextPrefixedId("e", edges.map((edge) => edge.id));
}

export function blankNode(id: string, type: string = "say"): IvrNode {
  const key = (type || "say").trim().toLowerCase() || "say";
  return {
    id,
    type: key,
    label: key === "start" ? "Start" : key.replace(/_/g, " "),
    prompt: "",
    min_digits: 1,
    max_digits: 8,
    terminator: "#",
    timeout_s: key === "wait" ? 1.2 : 8,
    sms_body: "",
    dest: "",
    kind: key === "dtmf" ? "generic" : "",
    charset: DEFAULT_DTMF_CHARSET,
    target: "",
    wait_for: key === "wait" ? "clock" : "",
    cause: key === "hangup" ? "NORMAL" : "",
    keys: key === "park" ? "ARS" : key === "wait_operator" ? "O" : "",
  };
}

export function blankGraph(slug: string, name: string): IvrGraph {
  const start = blankNode("start", "start");
  return {
    schema_version: 1,
    id: slug,
    name,
    start: start.id,
    nodes: [start],
    edges: [],
    system: false,
    description: "",
  };
}

export function graphIsSparse(graph: IvrGraph | null | undefined): boolean {
  if (!graph) return true;
  if (graph.nodes.length === 0) return true;
  if (graph.nodes.length === 1 && graph.edges.length === 0) return true;
  return false;
}

export function graphHasStart(graph: IvrGraph | null | undefined): boolean {
  if (!graph) return false;
  const start = (graph.start || "").trim();
  if (!start) return false;
  return graph.nodes.some((node) => node.id === start);
}

/** OTP / DTMF collect step present (gate 1/2, menu, or capture). */
export function graphHasCollect(graph: IvrGraph | null | undefined): boolean {
  if (!graph) return false;
  return graph.nodes.some((node) => {
    const type = (node.type || "").trim().toLowerCase();
    return type === "capture" || type === "dtmf" || type === "gate";
  });
}

function isDefaultWhen(when: string): boolean {
  const key = (when || "default").trim().toLowerCase();
  return key === "" || key === "default";
}

function snippetTypeAllowed(type: string): boolean {
  const key = (type || "").trim().toLowerCase();
  if (!key) return false;
  if ((FORBIDDEN_SNIPPET_TYPES as readonly string[]).includes(key)) return false;
  if ((DISABLED_NODE_TYPES as readonly string[]).includes(key)) return false;
  return true;
}

function snippetNode(id: string, type: string, extra: Partial<IvrNode> = {}): IvrNode {
  const base = blankNode(id, type);
  return { ...base, ...extra, id, type };
}

function snippetEdge(id: string, source: string, target: string, when = "default"): IvrEdge {
  return { id, source, target, when };
}

/**
 * Client-side Visual fragments when GET /api/ivr/snippets is missing.
 * First-party only. No originate / SMS / transfer / bank packs.
 */
export function fallbackSnippets(): IvrSnippet[] {
  const identPickup = snippetNode("ident_pickup", "pickup", { label: "Wait for human" });
  const identSay = snippetNode("ident_say", "say", {
    label: "Ident",
    prompt: "Press 1 to continue, or press 2 to decline.",
    file_id: "identification",
  });
  const identGate = snippetNode("ident_gate", "gate", {
    label: "Identification 1 / 2",
    file_id: "identification",
  });
  const holdMusic = snippetNode("hold_music", "hold", {
    label: "Hold music",
    file_id: "hold_comfort",
    timeout_s: 45,
  });
  const holdO = snippetNode("hold_o", "wait_operator", {
    label: "Press O when ready",
    keys: "O",
    timeout_s: 45,
  });
  const capture = snippetNode("capture_code", "capture", {
    label: "Capture code",
    file_id: "enter_code",
    min_digits: 4,
    max_digits: 8,
    terminator: "#",
  });
  const menuSay = snippetNode("menu_say", "say", {
    label: "Menu",
    prompt: "Press 1 to continue, or press 2 to decline.",
  });
  const menuKeys = snippetNode("menu_keys", "dtmf", {
    label: "DTMF 1 / 2",
    kind: "generic",
    charset: "12",
    min_digits: 1,
    max_digits: 1,
    terminator: "#",
    timeout_s: 8,
  });
  const say = snippetNode("say", "say", {
    label: "Say",
    prompt: "This is {{business_name}}. {{purpose}}.",
  });
  const hangup = snippetNode("hangup", "hangup", { label: "Hang up", cause: "NORMAL" });
  return [
    {
      id: "ident_12",
      label: "Add ident 1/2",
      description: "Wait for human, then identification press 1 or 2.",
      nodes: [identPickup, identSay, identGate],
      edges: [
        snippetEdge("e_ident_1", identPickup.id, identSay.id),
        snippetEdge("e_ident_2", identSay.id, identGate.id),
      ],
    },
    {
      id: "hold_o",
      label: "Add hold + O",
      description: "hold_comfort overlay until operator O. No digits on hold.",
      nodes: [holdMusic, holdO],
      edges: [snippetEdge("e_hold_1", holdMusic.id, holdO.id)],
    },
    {
      id: "capture_code",
      label: "Add capture code",
      description: "First-party enter_code capture (digits + #).",
      nodes: [capture],
      edges: [],
    },
    {
      id: "dtmf_menu",
      label: "Add DTMF menu",
      description: "Spoken menu then generic DTMF 1/2.",
      nodes: [menuSay, menuKeys],
      edges: [snippetEdge("e_menu_1", menuSay.id, menuKeys.id)],
    },
    {
      id: "say",
      label: "Add say",
      description: "One spoken line. Edit the prompt on the block.",
      nodes: [say],
      edges: [],
    },
    {
      id: "hangup",
      label: "Add hangup",
      description: "End the call (NORMAL).",
      nodes: [hangup],
      edges: [],
    },
  ];
}

function defaultPathTail(graph: Pick<IvrGraph, "start" | "nodes" | "edges">, from?: string): string | null {
  const start = (from || graph.start || graph.nodes[0]?.id || "").trim();
  if (!start) return null;
  const seen = new Set<string>();
  let cur = start;
  while (!seen.has(cur)) {
    seen.add(cur);
    const nxt = graph.edges.find((edge) => edge.source === cur && isDefaultWhen(edge.when));
    if (!nxt) return cur;
    cur = nxt.target;
  }
  return cur;
}

/**
 * Append a snippet onto *graph*. Remaps ids, adds a default edge from the
 * current tail (inserts before hangup when the default path already ends).
 * Does not wipe existing nodes.
 */
export function appendSnippet(graph: IvrGraph, snippet: Pick<IvrSnippet, "nodes" | "edges">): IvrGraph {
  const incoming = (snippet.nodes || []).filter((node) => snippetTypeAllowed(node.type));
  const fragment = incoming.filter((node) => (node.type || "").trim().toLowerCase() !== "start");

  let nodes = [...graph.nodes];
  let edges = [...graph.edges];
  let start = (graph.start || "").trim();

  if (nodes.length === 0) {
    const startNode = blankNode("start", "start");
    nodes = [startNode];
    start = startNode.id;
  } else if (!nodes.some((node) => node.id === start)) {
    const existingStart = nodes.find((node) => node.type === "start");
    if (existingStart) {
      start = existingStart.id;
    } else {
      const startNode = blankNode(nextNodeId(nodes), "start");
      nodes = [startNode, ...nodes];
      start = startNode.id;
    }
  }

  const added: IvrNode[] = [];
  const idMap = new Map<string, string>();
  for (const node of fragment) {
    const nid = nextNodeId([...nodes, ...added]);
    idMap.set(node.id, nid);
    added.push({ ...node, id: nid });
  }
  if (!added.length) {
    return { ...graph, start, nodes, edges };
  }

  const addedEdges: IvrEdge[] = [];
  for (const edge of snippet.edges || []) {
    const source = idMap.get(edge.source);
    const target = idMap.get(edge.target);
    if (!source || !target) continue;
    addedEdges.push({
      id: nextEdgeId([...edges, ...addedEdges]),
      source,
      target,
      when: (edge.when || "default").trim().toLowerCase() || "default",
    });
  }

  const firstId = added[0].id;
  const snippetTail =
    defaultPathTail({ start: firstId, nodes: added, edges: addedEdges }, firstId) || firstId;
  const tail = defaultPathTail({ start, nodes, edges }) || start;
  const tailNode = nodes.find((node) => node.id === tail);

  if (tailNode?.type === "hangup") {
    const incomingDefault = edges.find((edge) => edge.target === tail && isDefaultWhen(edge.when));
    if (incomingDefault) {
      edges = edges.map((edge) => (edge.id === incomingDefault.id ? { ...edge, target: firstId } : edge));
      addedEdges.push({
        id: nextEdgeId([...edges, ...addedEdges]),
        source: snippetTail,
        target: tail,
        when: "default",
      });
    } else if (!edges.some((edge) => edge.source === tail && isDefaultWhen(edge.when))) {
      addedEdges.push({
        id: nextEdgeId([...edges, ...addedEdges]),
        source: tail,
        target: firstId,
        when: "default",
      });
    }
  } else if (tail && !edges.some((edge) => edge.source === tail && isDefaultWhen(edge.when))) {
    addedEdges.push({
      id: nextEdgeId([...edges, ...addedEdges]),
      source: tail,
      target: firstId,
      when: "default",
    });
  } else {
    const dangling = [...nodes]
      .reverse()
      .find(
        (node) =>
          node.type !== "hangup" &&
          !edges.some((edge) => edge.source === node.id && isDefaultWhen(edge.when)),
      );
    if (dangling) {
      addedEdges.push({
        id: nextEdgeId([...edges, ...addedEdges]),
        source: dangling.id,
        target: firstId,
        when: "default",
      });
    }
  }

  return {
    ...graph,
    start,
    nodes: [...nodes, ...added],
    edges: [...edges, ...addedEdges],
  };
}

export function parseIvrSnippets(data: unknown): IvrSnippet[] {
  const record = asRecord(data);
  const rows = Array.isArray(data)
    ? data
    : Array.isArray(record.snippets)
      ? record.snippets
      : Array.isArray(record.items)
        ? record.items
        : [];
  const out: IvrSnippet[] = [];
  for (let i = 0; i < rows.length; i += 1) {
    const rec = asRecord(rows[i]);
    const nested =
      rec.graph && typeof rec.graph === "object" && !Array.isArray(rec.graph) ? asRecord(rec.graph) : rec;
    const rawNodes = Array.isArray(nested.nodes) ? nested.nodes : Array.isArray(rec.nodes) ? rec.nodes : [];
    const rawEdges = Array.isArray(nested.edges) ? nested.edges : Array.isArray(rec.edges) ? rec.edges : [];
    if (!rawNodes.length) continue;
    const tmp = normalizeGraph(
      {
        nodes: rawNodes,
        edges: rawEdges,
        start: str(nested.start || rec.start),
        id: str(rec.id, `snip-${i}`),
      },
      str(rec.id, `snip-${i}`),
    );
    const nodes = tmp.nodes.filter(
      (node) => snippetTypeAllowed(node.type) && (node.type || "").trim().toLowerCase() !== "start",
    );
    if (!nodes.length) continue;
    const known = new Set(nodes.map((node) => node.id));
    const edges = tmp.edges.filter((edge) => known.has(edge.source) && known.has(edge.target));
    out.push({
      id: str(rec.id || rec.slug, `snip-${i}`).trim() || `snip-${i}`,
      label: str(rec.label || rec.name, "Add step"),
      description: str(rec.description),
      nodes,
      edges,
    });
  }
  return out;
}

export async function listIvrSnippets(): Promise<IvrSnippet[]> {
  try {
    const data = await request<unknown>("/api/ivr/snippets");
    const rows = parseIvrSnippets(data);
    if (rows.length) return rows;
  } catch {
    /* 404 / 405 / network — client fragments stay available */
  }
  return fallbackSnippets();
}

function normalizeNode(raw: unknown, index: number): IvrNode {
  const data = asRecord(raw);
  const id = str(data.id, `n${index + 1}`).trim() || `n${index + 1}`;
  const type = str(data.type, "say").trim().toLowerCase() || "say";
  const matchRaw = data.match;
  const match =
    matchRaw && typeof matchRaw === "object" && !Array.isArray(matchRaw)
      ? Object.fromEntries(
          Object.entries(matchRaw as Record<string, unknown>).map(([key, value]) => [key, str(value)]),
        )
      : undefined;
  const node: IvrNode = {
    id,
    type,
    label: str(data.label),
    prompt: str(data.prompt),
    min_digits: num(data.min_digits, 1),
    max_digits: num(data.max_digits, 8),
    terminator: (str(data.terminator, "#").trim() || "#").slice(0, 1),
    timeout_s: num(data.timeout_s, 8),
    sms_body: str(data.sms_body),
    dest: str(data.dest),
    kind: str(data.kind, type === "dtmf" ? "generic" : "").trim().toLowerCase(),
    charset: str(data.charset, DEFAULT_DTMF_CHARSET).trim() || DEFAULT_DTMF_CHARSET,
  };
  const fileId = str(data.file_id).trim();
  const keys = str(data.keys).trim();
  const gateway = str(data.gateway).trim();
  const cause = str(data.cause).trim();
  const target = str(data.target).trim();
  const waitFor = str(data.wait_for).trim().toLowerCase();
  const promptClass = str(data.prompt_class).trim();
  const country = str(data.country).trim();
  const lang = str(data.lang).trim();
  if (fileId) node.file_id = fileId;
  if (keys) node.keys = keys;
  if (gateway) node.gateway = gateway;
  if (cause) node.cause = cause;
  if (target) node.target = target;
  if (waitFor) node.wait_for = waitFor;
  if (match && Object.keys(match).length) node.match = match;
  if (promptClass) node.prompt_class = promptClass;
  if (boolish(data.hitl)) node.hitl = true;
  if (country) node.country = country;
  if (lang) node.lang = lang;
  return node;
}

function normalizeEdge(raw: unknown, index: number): IvrEdge {
  const data = asRecord(raw);
  const source = str(data.source || data.from).trim();
  const target = str(data.target || data.to).trim();
  return {
    id: str(data.id, `e${index + 1}`).trim() || `e${index + 1}`,
    source,
    target,
    when: str(data.when, "default").trim().toLowerCase() || "default",
  };
}

/** Backend schema_version=1 caps (cdialer.ivr.schema). */
export const IVR_SCHEMA_VERSION = 1;
export const IVR_MAX_NODES = 512;
export const IVR_MAX_EDGES = 1024;
export const IVR_MAX_GOTO_JUMPS = 64;
export const IVR_MAX_WAIT_SILENCE_S = 30;
export const IVR_MAX_GRAPH_BYTES = 2 * 1024 * 1024;
export const IVR_NODE_TYPES = [
  "start",
  "say",
  "dtmf",
  "hold",
  "queue",
  "sms",
  "transfer",
  "gate",
  "capture",
  "park",
  "hangup",
  "identity",
  "pickup",
  "wait_operator",
  "wait",
  "label",
  "goto",
] as const;

export const SYSTEM_MAP =
  "AMD → wait-human → graph → DTMF → hold/O → park";

const IVR_NODE_TYPE_SET = new Set<string>(IVR_NODE_TYPES);

export function graphToJson(graph: IvrGraph): string {
  return `${JSON.stringify(graph, null, 2)}\n`;
}

/**
 * Parse an IvrGraph JSON document. Throws {@link IvrError} with a short detail
 * (never a 422 dump). Does not mutate the caller's previous graph.
 */
export function parseGraphJson(text: string, fallbackId = "script"): IvrGraph {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    throw new IvrError(400, "invalid JSON");
  }
  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) {
    throw new IvrError(400, "graph must be an object");
  }
  const record = raw as Record<string, unknown>;
  const wrapped = record.graph;
  const data: Record<string, unknown> =
    !Array.isArray(record.nodes) && wrapped && typeof wrapped === "object" && !Array.isArray(wrapped)
      ? (wrapped as Record<string, unknown>)
      : record;
  if (!Array.isArray(data.nodes)) {
    throw new IvrError(400, "nodes: Field required");
  }
  if (data.edges !== undefined && !Array.isArray(data.edges)) {
    throw new IvrError(400, "edges: Input should be a valid list");
  }
  const version =
    data.schema_version === undefined || data.schema_version === null
      ? IVR_SCHEMA_VERSION
      : num(data.schema_version, Number.NaN);
  if (version !== IVR_SCHEMA_VERSION) {
    throw new IvrError(400, `unsupported schema_version ${String(data.schema_version)}`);
  }
  if (data.nodes.length > IVR_MAX_NODES) {
    throw new IvrError(400, `too many nodes (${data.nodes.length} > ${IVR_MAX_NODES})`);
  }
  const edgesRaw = Array.isArray(data.edges) ? data.edges : [];
  if (edgesRaw.length > IVR_MAX_EDGES) {
    throw new IvrError(400, `too many edges (${edgesRaw.length} > ${IVR_MAX_EDGES})`);
  }
  let gotos = 0;
  for (let i = 0; i < data.nodes.length; i += 1) {
    const node = asRecord(data.nodes[i]);
    if (str(node.type).trim().toLowerCase() === "goto") gotos += 1;
  }
  if (gotos > IVR_MAX_GOTO_JUMPS) {
    throw new IvrError(400, `too many goto tags (${gotos} > ${IVR_MAX_GOTO_JUMPS})`);
  }
  for (let i = 0; i < data.nodes.length; i += 1) {
    const node = asRecord(data.nodes[i]);
    const id = str(node.id).trim();
    if (!id) throw new IvrError(400, `nodes.${i}.id: Field required`);
    const type = str(node.type).trim().toLowerCase();
    if (!type) throw new IvrError(400, `nodes.${i}.type: Field required`);
    if (!IVR_NODE_TYPE_SET.has(type)) {
      throw new IvrError(400, `nodes.${i}.type: unknown node type '${str(node.type)}'`);
    }
  }
  const graph = normalizeGraph({ ...data, schema_version: IVR_SCHEMA_VERSION, edges: edgesRaw }, fallbackId);
  const ids = graph.nodes.map((node) => node.id);
  if (ids.length !== new Set(ids).size) {
    throw new IvrError(400, "duplicate node id");
  }
  const known = new Set(ids);
  if (!known.has(graph.start)) {
    throw new IvrError(400, "start node missing");
  }
  const edgeIds = new Set<string>();
  for (const edge of graph.edges) {
    if (edgeIds.has(edge.id)) {
      throw new IvrError(400, `duplicate edge id ${edge.id}`);
    }
    edgeIds.add(edge.id);
    if (!known.has(edge.source) || !known.has(edge.target)) {
      throw new IvrError(400, `edge ${edge.id} references missing node`);
    }
  }
  return graph;
}

export function normalizeGraph(raw: unknown, fallbackId = "script"): IvrGraph {
  const data = asRecord(raw);
  const nodes = Array.isArray(data.nodes) ? data.nodes.map((row, index) => normalizeNode(row, index)) : [];
  const edges = Array.isArray(data.edges) ? data.edges.map((row, index) => normalizeEdge(row, index)) : [];
  const id = str(data.id, fallbackId).trim().toLowerCase() || fallbackId;
  const start = str(data.start).trim() || nodes[0]?.id || "start";
  return {
    schema_version: num(data.schema_version, 1) || 1,
    id,
    name: str(data.name, id),
    start,
    nodes,
    edges,
    system: boolish(data.system),
    description: str(data.description),
  };
}

export function normalizeSummary(raw: unknown, index = 0): IvrScriptSummary {
  const data = asRecord(raw);
  const slug = str(data.slug || data.id, `script-${index}`).trim().toLowerCase() || `script-${index}`;
  return {
    slug,
    name: str(data.name, slug),
    active_version: num(data.active_version, 1) || 1,
    system: boolish(data.system) || slug === "office",
    description: str(data.description),
    updated_at: str(data.updated_at),
  };
}

function versionList(raw: unknown): number[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => num(item, NaN))
    .filter((item) => Number.isFinite(item))
    .map((item) => Math.trunc(item));
}

export function normalizeScript(raw: unknown, fallbackSlug = "script"): IvrScript {
  const data = asRecord(raw);
  const nestedGraph = data.graph ?? data;
  const summary = normalizeSummary(data, 0);
  const slug = str(data.slug || data.id, fallbackSlug).trim().toLowerCase() || fallbackSlug;
  const graph = normalizeGraph(nestedGraph, slug);
  graph.id = slug;
  if (!graph.name) graph.name = summary.name;
  graph.system = summary.system || graph.system;
  const versions = versionList(data.versions);
  const version = num(data.version, summary.active_version) || summary.active_version;
  return {
    ...summary,
    slug,
    version,
    created_at: str(data.created_at),
    versions: versions.length ? versions : [version],
    graph,
  };
}

export function parseScriptList(data: unknown): IvrScriptSummary[] {
  if (Array.isArray(data)) return data.map((row, index) => normalizeSummary(row, index));
  const record = asRecord(data);
  const rows = record.scripts ?? record.items ?? record.rows ?? record.ivr;
  if (Array.isArray(rows)) return rows.map((row, index) => normalizeSummary(row, index));
  return [];
}

function payloadToScript(data: unknown, fallbackSlug: string): IvrScript {
  return normalizeScript(data, fallbackSlug);
}

/**
 * List IVR scripts. Throws {@link IvrError} (404 when the API is not live).
 */
export async function listIvrScripts(): Promise<IvrScriptSummary[]> {
  const data = await request<unknown>("/api/ivr");
  return parseScriptList(data);
}

export async function getIvrScript(slug: string): Promise<IvrScript> {
  const data = await request<unknown>(`/api/ivr/${encodeURIComponent(slug)}`);
  return payloadToScript(data, slug);
}

function slugifyName(name: string): string {
  const slug = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 63);
  if (!slug || !/^[a-z0-9]/.test(slug)) return "script";
  return slug;
}

export interface IvrTemplate {
  id: string;
  name: string;
  description: string;
  graph: IvrGraph;
}

export function parseIvrTemplates(data: unknown): IvrTemplate[] {
  const record = asRecord(data);
  const rows = Array.isArray(data)
    ? data
    : Array.isArray(record.templates)
      ? record.templates
      : Array.isArray(record.items)
        ? record.items
        : [];
  const out: IvrTemplate[] = [];
  const seen = new Set<string>();
  for (let i = 0; i < rows.length; i += 1) {
    const rec = asRecord(rows[i]);
    const id = str(rec.id || rec.slug).trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    const name = str(rec.name, id.replace(/_/g, " "));
    const description = str(rec.description);
    let graph: IvrGraph;
    try {
      graph = normalizeGraph(rec.graph ?? rec, id);
      graph.system = false;
      if (!graph.name) graph.name = name;
    } catch {
      graph = blankGraph(id, name);
    }
    out.push({ id, name, description, graph });
  }
  return out;
}

export async function listIvrTemplates(): Promise<IvrTemplate[]> {
  const data = await request<unknown>("/api/ivr/templates");
  return parseIvrTemplates(data);
}

export async function createIvrScript(name: string, clone?: string, template?: string): Promise<IvrScript> {
  const trimmed = name.trim();
  if (clone) {
    const data = await request<unknown>(`/api/ivr/${encodeURIComponent(clone)}/clone`, {
      method: "POST",
      body: { name: trimmed },
    });
    return payloadToScript(data, trimmed);
  }
  if (template) {
    const data = await request<unknown>("/api/ivr", {
      method: "POST",
      body: { name: trimmed || template, template },
    });
    return payloadToScript(data, trimmed || template);
  }
  const slug = slugifyName(trimmed);
  const graph = blankGraph(slug, trimmed || slug);
  const data = await request<unknown>("/api/ivr", {
    method: "POST",
    body: { name: trimmed || slug, slug, graph },
  });
  return payloadToScript(data, slug);
}

export async function saveIvrScript(slug: string, graph: IvrGraph, name?: string): Promise<IvrScript> {
  const data = await request<unknown>(`/api/ivr/${encodeURIComponent(slug)}`, {
    method: "PUT",
    body: { name: (name ?? graph.name).trim(), graph },
  });
  return payloadToScript(data, slug);
}

export async function activateIvrScript(slug: string, version: number): Promise<IvrScript> {
  const data = await request<unknown>(`/api/ivr/${encodeURIComponent(slug)}/activate`, {
    method: "POST",
    body: { version },
  });
  return payloadToScript(data, slug);
}

export interface ScriptIssue {
  path: string;
  pos: number;
  line: number;
  col: number;
  message: string;
}

export interface ScriptCatalogTag {
  name: string;
  flags: string[];
  attrs: string[];
  note: string;
  implemented?: boolean;
  forbidden?: boolean;
}

export interface ScriptCatalogNode {
  type: string;
  attrs: string[];
  children: string[];
  when: string[];
  implemented: boolean;
  disabled: boolean;
  forbidden: boolean;
  reason: string;
  note: string;
}

export interface ScriptCatalog {
  architecture?: string;
  tags: {
    known: string[];
    forbidden: string[];
    items: ScriptCatalogTag[];
    flags: Record<string, string[]>;
    attrs: Record<string, string[]>;
  };
  ivr: {
    node_types: string[];
    nodes?: ScriptCatalogNode[];
    edge_when: string[];
    max_nodes: number;
    max_edges: number;
    max_graph_bytes: number;
    schema_version: number;
    forbidden_types?: string[];
    dtmf: {
      kind: string[];
      charset: string;
      planned: boolean;
      note: string;
    };
    caps?: Record<string, number>;
  };
  pack_files: string[];
  caps: Record<string, number>;
  reserved_say_ids: string[];
  handler_events: string[];
  /** Present only when the live catalog lists template vars. */
  vars?: Array<string | { name?: string; token?: string; note?: string }>;
  /** Spoken `{{token}}` names from GET /api/scripts/catalog. */
  spoken_vars?: string[];
}

export interface CatalogDeskFeatures {
  vars: string[];
  countryLang: string[];
  caps: Record<string, number>;
}

function pushName(target: Set<string>, value: unknown): void {
  if (typeof value === "string" && value.trim()) target.add(value.trim());
}

/**
 * Vars and country/lang branches the Docs tab may show.
 * Only names the catalog actually lists — never a hardcoded token set.
 */
export function catalogDeskFeatures(catalog: ScriptCatalog | null): CatalogDeskFeatures {
  if (!catalog) return { vars: [], countryLang: [], caps: {} };
  const vars = new Set<string>();
  for (const item of catalog.vars ?? []) {
    if (typeof item === "string") pushName(vars, item);
    else if (item && typeof item === "object") {
      pushName(vars, item.name);
      pushName(vars, item.token);
    }
  }
  for (const name of catalog.spoken_vars ?? []) pushName(vars, name);
  const countryLang: string[] = [];
  const whenAttrs = catalog.tags?.attrs?.when ?? [];
  for (const attr of whenAttrs) {
    if (attr === "country" || attr === "lang") countryLang.push(`when ${attr}=`);
  }
  const sayAttrs = catalog.tags?.attrs?.say ?? [];
  for (const attr of sayAttrs) {
    if (attr === "country" || attr === "lang") countryLang.push(`say ${attr}=`);
  }
  for (const edge of catalog.ivr?.edge_when ?? []) {
    if (/country|lang|locale/i.test(edge)) countryLang.push(edge);
  }
  return { vars: [...vars], countryLang, caps: catalog.caps || {} };
}

export function catalogPalette(catalog: ScriptCatalog | null): ScriptCatalogNode[] {
  const rows = catalog?.ivr?.nodes;
  if (rows && rows.length) return rows;
  return DESIGNER_NODE_TYPES.map((type) => {
    const disabled = nodeIsDisabled(type);
    return {
      type,
      attrs: [],
      children: [],
      when: [],
      implemented: !disabled,
      disabled,
      forbidden: false,
      reason: disabled ? "walker does not run this node" : "",
      note: "",
    };
  });
}

export interface ScriptValidateResult {
  ok: boolean;
  errors: ScriptIssue[];
}

function asIssue(raw: unknown): ScriptIssue {
  const data = asRecord(raw);
  return {
    path: str(data.path, "text"),
    pos: num(data.pos, 0),
    line: num(data.line, 1) || 1,
    col: num(data.col, 1) || 1,
    message: str(data.message, "invalid"),
  };
}

export function parseValidateResult(raw: unknown): ScriptValidateResult {
  const data = asRecord(raw);
  const rows = Array.isArray(data.errors) ? data.errors.map(asIssue) : [];
  return { ok: data.ok === true && rows.length === 0, errors: rows };
}

export function formatScriptIssue(issue: ScriptIssue): string {
  return `${issue.path}:${issue.line}:${issue.col} ${issue.message}`;
}

export async function getScriptCatalog(): Promise<ScriptCatalog> {
  return request<ScriptCatalog>("/api/scripts/catalog");
}

export async function validateScript(body: {
  kind: "script" | "ivr";
  text?: string;
  graph?: unknown;
}): Promise<ScriptValidateResult> {
  const data = await request<unknown>("/api/scripts/validate", { method: "POST", body });
  return parseValidateResult(data);
}
